import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://api.schoolpay.tech";

const PAJSK_COLORS = {
    primary: "#E94057",
    orange: "#F97316",
    purple: "#8A2387",
    gradientEnd: "#F27121",
    text: "#111827",
    muted: "#6B7280",
    border: "#EEF2F7",
    bg: "#F8FAFC",
};

const COMPONENT_OPTIONS = [
    { value: "", label: "Semua Komponen" },
    { value: "BADAN_BERUNIFORM", label: "Badan Beruniform" },
    { value: "KELAB_PERSATUAN", label: "Kelab / Persatuan" },
    { value: "SUKAN_PERMAINAN", label: "Sukan / Permainan" },
    { value: "EKSTRA_KURIKULUM", label: "Ekstra Kurikulum" },
    { value: "SEGAK", label: "SEGAK" },
    { value: "LAIN_LAIN", label: "Lain-lain" },
];

const ACTIVITY_COMPONENT_OPTIONS = COMPONENT_OPTIONS.filter((item) => item.value);

const STATUS_OPTIONS = [
    { value: "", label: "Semua Status" },
    { value: "DRAF", label: "Draf" },
    { value: "DIHANTAR", label: "Dihantar" },
    { value: "DISAHKAN", label: "Disahkan" },
    { value: "DITOLAK", label: "Ditolak" },
];

const CATEGORY_MAP = {
    BADAN_BERUNIFORM: "Badan Beruniform",
    KELAB_PERSATUAN: "Kelab / Persatuan",
    SUKAN_PERMAINAN: "Sukan / Permainan",
    EKSTRA_KURIKULUM: "Ekstra Kurikulum",
    SEGAK: "SEGAK",
    LAIN_LAIN: "Lain-lain",
};

const STATUS_META = {
    DRAF: { label: "Draf", className: "statusDraft" },
    DIHANTAR: { label: "Dihantar", className: "statusSubmitted" },
    DISAHKAN: { label: "Disahkan", className: "statusVerified" },
    DITOLAK: { label: "Ditolak", className: "statusRejected" },
};

const getActivityCategoryFromComponent = (componentType) => {
    if (componentType === "BADAN_BERUNIFORM") return "BADAN_BERUNIFORM";
    if (componentType === "KELAB_PERSATUAN") return "KELAB_PERSATUAN";
    if (componentType === "SUKAN_PERMAINAN") return "SUKAN_PERMAINAN";
    return "LAIN_LAIN";
};

const getToken = () => {
    return localStorage.getItem("token") || localStorage.getItem("secure_token") || "";
};

const authHeaders = () => ({
    "Content-Type": "application/json",
    token: getToken(),
});

const normalizeResponseData = (json) => {
    if (!json) return null;
    return json.data;
};

const getRecordsFromResponse = (json) => {
    const data = normalizeResponseData(json);

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.records)) return data.records;

    return [];
};

const getPaginationFromResponse = (json) => {
    const data = normalizeResponseData(json);
    return data?.pagination || null;
};

const getStudentName = (record) => {
    const joined = `${record?.first_name || ""} ${record?.last_name || ""}`.trim();
    return joined || record?.moeis_name || record?.student_name || record?.student_uuid || "-";
};

const formatDateTime = (value) => {
    if (!value) return "-";

    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";

        return date.toLocaleString("ms-MY", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "-";
    }
};

const getRecordDateTime = (record) => {
    return (
        record?.submitted_at ||
        record?.created_at ||
        record?.updated_at ||
        record?.recorded_at ||
        record?.date ||
        null
    );
};
const getPersonLabel = (value, fallback = "-") => {
    if (!value) return fallback;

    const text = String(value).trim();

    if (!text) return fallback;
    if (text === "portal") return "Portal";

    return text;
};

const getRecordedByLabel = (record) => {
    return getPersonLabel(
        record?.recorded_by_name ||
        record?.recorded_by_email ||
        record?.recorded_by,
        "-"
    );
};

const getVerifiedByLabel = (record) => {
    return getPersonLabel(
        record?.verified_by_name ||
        record?.verified_by_email ||
        record?.verified_by,
        "-"
    );
};


const getDisplayStatus = (record) => {
    const rawStatus = String(record?.status || "").trim().toUpperCase();

    if (rawStatus) return rawStatus;

    if (record?.verified_at || record?.verified_by || record?.verified_by_name) {
        return "DISAHKAN";
    }

    return "DIHANTAR";
};
const getSourceLabel = (activity) => {
    if (activity?.source_type === "school_activity") return "Sekolah";
    if (activity?.source_type === "master_activity") return "Template";
    return "Aktiviti";
};

const normalizeActivity = (item, index = 0) => {
    const sourceType =
        item?.source_type || (item?.master_activity_id ? "master_activity" : "school_activity");

    const schoolActivityId = item?.id || item?.activity_id || null;
    const masterActivityId = item?.master_activity_id || null;

    return {
        ...item,
        id: sourceType === "school_activity" ? schoolActivityId : null,
        activity_id: sourceType === "school_activity" ? schoolActivityId : null,
        master_activity_id: sourceType === "master_activity" ? masterActivityId : null,
        source_type: sourceType,
        activity_name: item?.activity_name || item?.name || item?.title || "Aktiviti",
        activity_category:
            item?.activity_category ||
            getActivityCategoryFromComponent(item?.component_type) ||
            "LAIN_LAIN",
        component_type: item?.component_type || "LAIN_LAIN",
        temp_key: `${sourceType}-${schoolActivityId || masterActivityId || index}`,
    };
};

const EmptyState = ({ title, description }) => (
    <div className="emptyState">
        <div className="emptyIcon">—</div>
        <div>
            <div className="emptyTitle">{title}</div>
            <div className="emptyDescription">{description}</div>
        </div>
    </div>
);

const StatCard = ({ label, value, helper, tone = "default" }) => (
    <div className={`statCard ${tone}`}>
        <div className="statLabel">{label}</div>
        <div className="statValue">{value}</div>
        {!!helper && <div className="statHelper">{helper}</div>}
    </div>
);

const PAJSKDashboard = () => {
    const currentYear = new Date().getFullYear();

    const [academicYear, setAcademicYear] = useState(currentYear);
    const [componentFilter, setComponentFilter] = useState("");
    const [activityKeyword, setActivityKeyword] = useState("");
    const [recordKeyword, setRecordKeyword] = useState("");
    const [recordStatus, setRecordStatus] = useState("");

    const [activities, setActivities] = useState([]);
    const [selectedActivity, setSelectedActivity] = useState(null);

    const [activityPagination, setActivityPagination] = useState(null);
    const [recordPagination, setRecordPagination] = useState(null);

    const [records, setRecords] = useState([]);

    const [activitiesLoading, setActivitiesLoading] = useState(false);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const [activityPage, setActivityPage] = useState(1);
    const [recordPage, setRecordPage] = useState(1);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newActivityName, setNewActivityName] = useState("");
    const [newComponentType, setNewComponentType] = useState("SUKAN_PERMAINAN");
    const [newVenue, setNewVenue] = useState("");
    const [newOrganizer, setNewOrganizer] = useState("");
    const [newDescription, setNewDescription] = useState("");

    const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
    const [selectedEvidenceRecord, setSelectedEvidenceRecord] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [viewingAttachmentId, setViewingAttachmentId] = useState(null);
    const [decryptedImage, setDecryptedImage] = useState(null);

    const selectedActivityId = selectedActivity?.id || selectedActivity?.activity_id || null;

    const selectedIsSchoolActivity =
        selectedActivity?.source_type === "school_activity" && !!selectedActivityId;

    const fetchActivities = async (page = activityPage) => {
        try {
            setActivitiesLoading(true);

            const params = new URLSearchParams();
            params.set("academic_year", String(academicYear));
            params.set("page", String(page));
            params.set("limit", "20");

            if (componentFilter) params.set("component_type", componentFilter);
            if (activityKeyword.trim()) params.set("keyword", activityKeyword.trim());

            const response = await fetch(
                `${API_BASE_URL}/pajsk/activity/list?${params.toString()}`,
                {
                    method: "GET",
                    headers: authHeaders(),
                }
            );

            const json = await response.json();

            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal mendapatkan aktiviti PAJSK.");
            }

            const nextActivities = getRecordsFromResponse(json).map(normalizeActivity);

            setActivities(nextActivities);
            setActivityPagination(getPaginationFromResponse(json));

            if (!selectedActivity && nextActivities.length > 0) {
                const firstSchoolActivity =
                    nextActivities.find((item) => item.source_type === "school_activity") ||
                    nextActivities[0];

                setSelectedActivity(firstSchoolActivity);
            }

            if (selectedActivity) {
                const stillExists = nextActivities.find(
                    (item) => item.temp_key === selectedActivity.temp_key
                );

                if (!stillExists && nextActivities.length > 0) {
                    const firstSchoolActivity =
                        nextActivities.find((item) => item.source_type === "school_activity") ||
                        nextActivities[0];

                    setSelectedActivity(firstSchoolActivity);
                }
            }
        } catch (error) {
            console.error("fetchActivities error:", error);
            alert(error.message || "Gagal mendapatkan aktiviti PAJSK.");
            setActivities([]);
            setActivityPagination(null);
        } finally {
            setActivitiesLoading(false);
        }
    };

    const fetchRecordsByActivity = async (page = recordPage) => {
        if (!selectedIsSchoolActivity) {
            setRecords([]);
            setRecordPagination(null);
            return;
        }

        try {
            setRecordsLoading(true);

            const params = new URLSearchParams();
            params.set("page", String(page));
            params.set("limit", "20");

            if (recordStatus) params.set("status", recordStatus);
            if (recordKeyword.trim()) params.set("keyword", recordKeyword.trim());

            const response = await fetch(
                `${API_BASE_URL}/pajsk/records/by-activity/${selectedActivityId}?${params.toString()}`,
                {
                    method: "GET",
                    headers: authHeaders(),
                }
            );

            const json = await response.json();

            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal mendapatkan rekod PAJSK.");
            }

            setRecords(getRecordsFromResponse(json));
            setRecordPagination(getPaginationFromResponse(json));
        } catch (error) {
            console.error("fetchRecordsByActivity error:", error);
            alert(error.message || "Gagal mendapatkan rekod PAJSK.");
            setRecords([]);
            setRecordPagination(null);
        } finally {
            setRecordsLoading(false);
        }
    };

    const createActivity = async () => {
        if (!newActivityName.trim()) {
            alert("Sila masukkan nama aktiviti.");
            return;
        }

        try {
            setCreating(true);

            const payload = {
                academic_year: Number(academicYear || currentYear),
                activity_name: newActivityName.trim(),
                activity_category: getActivityCategoryFromComponent(newComponentType),
                component_type: newComponentType,
                organizer: newOrganizer || null,
                venue: newVenue || null,
                description: newDescription || null,
            };

            const response = await fetch(`${API_BASE_URL}/pajsk/activity/create`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(payload),
            });

            const json = await response.json();

            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal mencipta aktiviti PAJSK.");
            }

            const created = normalizeActivity({
                ...json.data,
                source_type: "school_activity",
                is_created: 1,
            });

            setSelectedActivity(created);
            setCreateModalOpen(false);

            setNewActivityName("");
            setNewComponentType("SUKAN_PERMAINAN");
            setNewVenue("");
            setNewOrganizer("");
            setNewDescription("");

            setActivityPage(1);
            await fetchActivities(1);
        } catch (error) {
            console.error("createActivity error:", error);
            alert(error.message || "Gagal mencipta aktiviti PAJSK.");
        } finally {
            setCreating(false);
        }
    };
    const verifyRecord = async (recordId) => {
        try {
            setActionLoadingId(recordId);

            const response = await fetch(`${API_BASE_URL}/pajsk/record/verify/${recordId}`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({}),
            });

            const json = await response.json();
            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal mengesahkan rekod.");
            }

            // Update UI immediately
            setRecords((prevRecords) =>
                prevRecords.map((record) =>
                    record.id === recordId
                        ? {
                            ...record,
                            status: "DISAHKAN",
                            verified_at: new Date().toISOString(),
                        }
                        : record
                )
            );

            // Optional backend sync refresh
            await fetchRecordsByActivity(recordPage);
        } catch (error) {
            console.error("verifyRecord error:", error);
            alert(error.message || "Gagal mengesahkan rekod.");
        } finally {
            setActionLoadingId(null);
        }
    };

    const rejectRecord = async (recordId) => {
        const remarks = window.prompt("Sebab ditolak / catatan:");
        if (remarks === null) return;

        try {
            setActionLoadingId(recordId);

            const response = await fetch(`${API_BASE_URL}/pajsk/record/reject/${recordId}`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({
                    remarks,
                }),
            });

            const json = await response.json();

            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal menolak rekod.");
            }

            // Update UI immediately
            setRecords((prevRecords) =>
                prevRecords.map((record) =>
                    record.id === recordId
                        ? {
                            ...record,
                            status: "DITOLAK",
                            remarks: remarks || record.remarks,
                            verified_at: new Date().toISOString(),
                        }
                        : record
                )
            );

            // Optional backend sync refresh
            await fetchRecordsByActivity(recordPage);
        } catch (error) {
            console.error("rejectRecord error:", error);
            alert(error.message || "Gagal menolak rekod.");
        } finally {
            setActionLoadingId(null);
        }
    };
    const openEvidence = async (record) => {
        setSelectedEvidenceRecord(record);
        setEvidenceModalOpen(true);
        setAttachments([]);
        setDecryptedImage(null);

        try {
            setAttachmentsLoading(true);

            const response = await fetch(`${API_BASE_URL}/pajsk/attachments/${record.id}`, {
                method: "GET",
                headers: authHeaders(),
            });

            const json = await response.json();

            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal mendapatkan eviden.");
            }

            const data = normalizeResponseData(json);
            setAttachments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("openEvidence error:", error);
            alert(error.message || "Gagal mendapatkan eviden.");
            setAttachments([]);
        } finally {
            setAttachmentsLoading(false);
        }
    };

    const viewAttachmentImage = async (attachmentId) => {
        try {
            setViewingAttachmentId(attachmentId);
            setDecryptedImage(null);

            const response = await fetch(`${API_BASE_URL}/pajsk/attachment/view/${attachmentId}`, {
                method: "GET",
                headers: authHeaders(),
            });

            const json = await response.json();

            if (json.response_code !== 1000) {
                throw new Error(json.message || "Gagal membuka eviden.");
            }

            const data = normalizeResponseData(json);
            setDecryptedImage(data?.image_data || null);
        } catch (error) {
            console.error("viewAttachmentImage error:", error);
            alert(error.message || "Gagal membuka eviden.");
        } finally {
            setViewingAttachmentId(null);
        }
    };

    const closeEvidenceModal = () => {
        setEvidenceModalOpen(false);
        setSelectedEvidenceRecord(null);
        setAttachments([]);
        setDecryptedImage(null);
        setViewingAttachmentId(null);
    };

    useEffect(() => {
        setActivityPage(1);
        fetchActivities(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [academicYear, componentFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setActivityPage(1);
            fetchActivities(1);
        }, 450);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityKeyword]);

    useEffect(() => {
        setRecordPage(1);
        fetchRecordsByActivity(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedActivityId, selectedActivity?.source_type, recordStatus]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setRecordPage(1);
            fetchRecordsByActivity(1);
        }, 450);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordKeyword]);

    const dashboardStats = useMemo(() => {
        const totalActivities = activityPagination?.total_records ?? activities.length;
        const schoolCreated = activities.filter((item) => item.source_type === "school_activity").length;
        const templates = activities.filter((item) => item.source_type === "master_activity").length;

        const totalRecords = recordPagination?.total_records ?? records.length;
        const verified = records.filter((item) => item.status === "DISAHKAN").length;
        const rejected = records.filter((item) => item.status === "DITOLAK").length;
        const draft = records.filter((item) => item.status === "DRAF").length;
        const submitted = records.filter((item) => item.status === "DIHANTAR").length;

        return {
            totalActivities,
            schoolCreated,
            templates,
            totalRecords,
            verified,
            rejected,
            draft,
            submitted,
        };
    }, [activityPagination, activities, recordPagination, records]);

    const handleActivityPageChange = async (nextPage) => {
        setActivityPage(nextPage);
        await fetchActivities(nextPage);
    };

    const handleRecordPageChange = async (nextPage) => {
        setRecordPage(nextPage);
        await fetchRecordsByActivity(nextPage);
    };

    return (
        <div className="pajskPage">
            <style>{styles}</style>

            <div className="pageShell">
                <section className="heroPanel">
                    <div>
                        <div className="eyebrow">PAJSK</div>
                        <h1>Semakan Rekod PAJSK</h1>
                        <p>
                            Pilih aktiviti, semak rekod murid, lihat eviden, kemudian sahkan atau tolak penyertaan.
                        </p>
                    </div>

                    <div className="heroActions">
                        <button className="ghostButton" onClick={() => fetchActivities(activityPage)}>
                            Refresh
                        </button>
                        <button className="primaryButton" onClick={() => setCreateModalOpen(true)}>
                            + Aktiviti Baharu
                        </button>
                    </div>
                </section>

                <section className="filtersPanel">
                    <div className="filterGroup">
                        <label>Tahun Akademik</label>
                        <input
                            value={academicYear}
                            onChange={(event) => setAcademicYear(event.target.value)}
                            type="number"
                        />
                    </div>

                    <div className="filterGroup wide">
                        <label>Cari Aktiviti</label>
                        <input
                            value={activityKeyword}
                            onChange={(event) => setActivityKeyword(event.target.value)}
                            placeholder="Cari aktiviti..."
                        />
                    </div>

                    <div className="filterGroup">
                        <label>Komponen</label>
                        <select
                            value={componentFilter}
                            onChange={(event) => setComponentFilter(event.target.value)}
                        >
                            {COMPONENT_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </section>

                <section className="statsGrid">
                    <StatCard
                        label="Aktiviti"
                        value={dashboardStats.totalActivities}
                        helper="Jumlah aktiviti tahun ini"
                        tone="purple"
                    />
                    <StatCard
                        label="Rekod"
                        value={dashboardStats.totalRecords}
                        helper="Untuk aktiviti dipilih"
                        tone="red"
                    />
                    <StatCard
                        label="Disahkan"
                        value={dashboardStats.verified}
                        helper="Telah disemak"
                        tone="green"
                    />
                    <StatCard
                        label="Menunggu"
                        value={dashboardStats.submitted}
                        helper="Perlu semakan"
                        tone="orange"
                    />
                </section>

                <section className="mainGrid">
                    <div className="leftColumn">
                        <div className="panel activityPanel">
                            <div className="panelHeader compactHeader">
                                <div>
                                    <h2>Senarai Aktiviti</h2>
                                    <p>Pilih aktiviti untuk semakan murid.</p>
                                </div>
                                <span className="plainCount">
                                    {activitiesLoading ? "Memuatkan..." : `${activities.length} rekod`}
                                </span>
                            </div>

                            <div className="activityTableWrap">
                                {activitiesLoading ? (
                                    <div className="loadingBlock">Memuatkan aktiviti...</div>
                                ) : activities.length === 0 ? (
                                    <EmptyState
                                        title="Tiada aktiviti"
                                        description="Cipta aktiviti baharu atau ubah carian."
                                    />
                                ) : (
                                    <div className="activityCardList">
                                        {activities.map((activity) => {
                                            const active = selectedActivity?.temp_key === activity.temp_key;
                                            const pendingCount = Number(activity.pending_count || 0);
                                            const totalRecords = Number(activity.total_records || 0);
                                            const verifiedCount = Number(activity.verified_count || 0);
                                            const rejectedCount = Number(activity.rejected_count || 0);

                                            return (
                                                <button
                                                    type="button"
                                                    key={activity.temp_key}
                                                    className={`activityCard ${active ? "active" : ""} ${pendingCount > 0 ? "hasPendingReview" : ""
                                                        }`}
                                                    onClick={() => setSelectedActivity(activity)}
                                                >
                                                    <div className="activityCardTop">
                                                        <div className="activityIcon">
                                                            {(activity.activity_name || "A").charAt(0).toUpperCase()}
                                                        </div>

                                                        <div className="activityCardMain">
                                                            <div className="activityTitleRow">
                                                                <h3>{activity.activity_name}</h3>

                                                                <span
                                                                    className={`pendingBadge ${pendingCount > 0 ? "hasPending" : "noPending"
                                                                        }`}
                                                                >
                                                                    {pendingCount} menunggu
                                                                </span>
                                                            </div>

                                                            <div className="activityMetaLine">
                                                                <span>
                                                                    {CATEGORY_MAP[activity.component_type] ||
                                                                        activity.component_type ||
                                                                        "Komponen tidak ditetapkan"}
                                                                </span>

                                                                <span className="metaDot">•</span>

                                                                <span>
                                                                    {activity.organizer ||
                                                                        activity.venue ||
                                                                        getSourceLabel(activity)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="activityStatsMini">
                                                        <div>
                                                            <strong>{totalRecords}</strong>
                                                            <span>Rekod</span>
                                                        </div>

                                                        <div>
                                                            <strong>{verifiedCount}</strong>
                                                            <span>Disahkan</span>
                                                        </div>

                                                        <div>
                                                            <strong>{rejectedCount}</strong>
                                                            <span>Ditolak</span>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {!!activityPagination && (
                                <div className="paginationRow">
                                    <button
                                        disabled={!activityPagination.has_prev}
                                        onClick={() => handleActivityPageChange(activityPage - 1)}
                                    >
                                        Sebelum
                                    </button>
                                    <span>
                                        Halaman {activityPagination.page} /{" "}
                                        {activityPagination.total_pages || 1}
                                    </span>
                                    <button
                                        disabled={!activityPagination.has_next}
                                        onClick={() => handleActivityPageChange(activityPage + 1)}
                                    >
                                        Seterusnya
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rightColumn">
                        <div className="panel">
                            <div className="panelHeader tableHeader">
                                <div>
                                    <h2>Rekod Murid</h2>
                                    <p>
                                        {selectedActivity
                                            ? selectedActivity.activity_name
                                            : "Pilih aktiviti untuk melihat rekod murid."}
                                    </p>
                                </div>

                                <div className="tableFilters">
                                    <input
                                        value={recordKeyword}
                                        onChange={(event) => setRecordKeyword(event.target.value)}
                                        placeholder="Cari murid..."
                                    />

                                    <select
                                        value={recordStatus}
                                        onChange={(event) => setRecordStatus(event.target.value)}
                                    >
                                        {STATUS_OPTIONS.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="tableWrap">
                                {recordsLoading ? (
                                    <div className="loadingBlock">Memuatkan rekod murid...</div>
                                ) : !selectedActivity ? (
                                    <EmptyState
                                        title="Pilih aktiviti"
                                        description="Rekod murid akan dipaparkan selepas aktiviti dipilih."
                                    />
                                ) : !selectedIsSchoolActivity ? (
                                    <EmptyState
                                        title="Template master dipilih"
                                        description="Pilih aktiviti sekolah untuk melihat rekod murid."
                                    />
                                ) : records.length === 0 ? (
                                    <EmptyState
                                        title="Tiada rekod murid"
                                        description="Belum ada murid direkodkan untuk aktiviti ini."
                                    />
                                ) : (
                                    <div className="recordList">
                                        {records.map((record) => {
                                            const displayStatus = getDisplayStatus(record);

                                            const statusMeta = STATUS_META[displayStatus] || {
                                                label: displayStatus || "-",
                                                className: "statusDraft",
                                            };

                                            const studentName = getStudentName(record);
                                            const hasEvidence = Number(record.attachment_count || 0) > 0;
                                            const dateText = formatDateTime(getRecordDateTime(record));
                                            const recordedByText = getRecordedByLabel(record);
                                            const verifiedByText = getVerifiedByLabel(record);


                                            const isFinalStatus = displayStatus === "DISAHKAN" || displayStatus === "DITOLAK";

                                            return (
                                                <article className="recordCard" key={record.id}>
                                                    <div className="recordCardTop">
                                                        <div className="studentCell recordStudentCell">
                                                            <div className="avatar">
                                                                {record.profile_pic ? (
                                                                    <img
                                                                        src={record.profile_pic}
                                                                        alt={studentName}
                                                                        className="studentAvatarImg"
                                                                        onError={(event) => {
                                                                            event.currentTarget.style.display = "none";
                                                                            event.currentTarget.parentElement.classList.add("avatarFallback");
                                                                            event.currentTarget.parentElement.textContent = studentName
                                                                                .charAt(0)
                                                                                .toUpperCase();
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    studentName.charAt(0).toUpperCase()
                                                                )}
                                                            </div>

                                                            <div className="studentTextBlock">
                                                                <div className="recordStudentName">
                                                                    {studentName}
                                                                </div>

                                                                <div className="recordMetaLine">
                                                                    {record.class_name && <span>{record.class_name}</span>}

                                                                    {dateText !== "-" && (
                                                                        <>
                                                                            <span className="metaDot">•</span>
                                                                            <span>{dateText}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <span className={`statusBadge ${statusMeta.className}`}>
                                                            {statusMeta.label}
                                                        </span>
                                                    </div>
                                                    <div className="recordSummaryRow">
                                                        <div className="summaryItem">
                                                            <span>Peringkat</span>
                                                            <strong>{record.level || "-"}</strong>
                                                        </div>

                                                        <div className="summaryItem">
                                                            <span>Peranan</span>
                                                            <strong>{record.role || "-"}</strong>
                                                        </div>

                                                        <div className="summaryItem personItem">
                                                            <span>Direkod oleh</span>
                                                            <strong>{recordedByText}</strong>
                                                        </div>

                                                        <div className="summaryItem personItem">
                                                            <span>Disahkan oleh</span>
                                                            <strong>
                                                                {record.verified_by || record.verified_by_name || record.verified_at
                                                                    ? verifiedByText
                                                                    : "Belum disahkan"}
                                                            </strong>
                                                        </div>
                                                    </div>

                                                    <div className="recordNotePanel">
                                                        <div>
                                                            <span>Catatan / Pencapaian</span>
                                                            <strong>{record.remarks || record.achievement || "Tiada catatan"}</strong>
                                                        </div>
                                                    </div>
                                                    <div className="recordCardBottom">
                                                        <div className="evidenceSummary">
                                                            {hasEvidence ? (
                                                                <>
                                                                    <span className="evidenceDot" />
                                                                    <span>{record.attachment_count} eviden dilampirkan</span>
                                                                </>
                                                            ) : (
                                                                <span className="noEvidenceText">
                                                                    Tiada eviden dilampirkan
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="recordActions">
                                                            {hasEvidence && (
                                                                <button
                                                                    className="evidenceButton"
                                                                    onClick={() => openEvidence(record)}
                                                                >
                                                                    Lihat Eviden
                                                                </button>
                                                            )}

                                                            <button
                                                                className="verifyButton"
                                                                disabled={actionLoadingId === record.id || displayStatus === "DISAHKAN"}
                                                                onClick={() => verifyRecord(record.id)}
                                                            >
                                                                {displayStatus === "DISAHKAN" ? "Telah Lulus" : "Luluskan"}
                                                            </button>

                                                            <button
                                                                className="rejectButton"
                                                                disabled={actionLoadingId === record.id || displayStatus === "DITOLAK"}
                                                                onClick={() => rejectRecord(record.id)}
                                                            >
                                                                {displayStatus === "DITOLAK" ? "Telah Ditolak" : "Minta Pembetulan"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {!!recordPagination && (
                                <div className="paginationRow">
                                    <button
                                        disabled={!recordPagination.has_prev}
                                        onClick={() => handleRecordPageChange(recordPage - 1)}
                                    >
                                        Sebelum
                                    </button>
                                    <span>
                                        Halaman {recordPagination.page} /{" "}
                                        {recordPagination.total_pages || 1}
                                    </span>
                                    <button
                                        disabled={!recordPagination.has_next}
                                        onClick={() => handleRecordPageChange(recordPage + 1)}
                                    >
                                        Seterusnya
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>

            {createModalOpen && (
                <div className="modalOverlay" onMouseDown={() => setCreateModalOpen(false)}>
                    <div className="modalCard" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="modalHeader">
                            <div>
                                <h2>Tambah Aktiviti PAJSK</h2>
                                <p>Cipta aktiviti sekolah untuk rekod murid dan eviden.</p>
                            </div>

                            <button className="closeButton" onClick={() => setCreateModalOpen(false)}>
                                ×
                            </button>
                        </div>

                        <div className="formGrid">
                            <div className="formGroup full">
                                <label>Nama Aktiviti</label>
                                <input
                                    value={newActivityName}
                                    onChange={(event) => setNewActivityName(event.target.value)}
                                    placeholder="Contoh: Kejohanan Bola Sepak Daerah"
                                />
                            </div>

                            <div className="formGroup">
                                <label>Komponen</label>
                                <select
                                    value={newComponentType}
                                    onChange={(event) => setNewComponentType(event.target.value)}
                                >
                                    {ACTIVITY_COMPONENT_OPTIONS.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="formGroup">
                                <label>Penganjur</label>
                                <input
                                    value={newOrganizer}
                                    onChange={(event) => setNewOrganizer(event.target.value)}
                                    placeholder="Contoh: PPD / Sekolah"
                                />
                            </div>

                            <div className="formGroup">
                                <label>Tempat</label>
                                <input
                                    value={newVenue}
                                    onChange={(event) => setNewVenue(event.target.value)}
                                    placeholder="Contoh: Dewan sekolah"
                                />
                            </div>

                            <div className="formGroup full">
                                <label>Deskripsi</label>
                                <textarea
                                    value={newDescription}
                                    onChange={(event) => setNewDescription(event.target.value)}
                                    placeholder="Catatan ringkas aktiviti"
                                    rows={4}
                                />
                            </div>
                        </div>

                        <div className="modalActions">
                            <button className="ghostButton" onClick={() => setCreateModalOpen(false)}>
                                Batal
                            </button>
                            <button className="primaryButton" disabled={creating} onClick={createActivity}>
                                {creating ? "Menyimpan..." : "Simpan Aktiviti"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {evidenceModalOpen && (
                <div className="modalOverlay" onMouseDown={closeEvidenceModal}>
                    <div
                        className="modalCard evidenceModal"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className="modalHeader">
                            <div>
                                <h2>Eviden Murid</h2>
                                <p>
                                    {selectedEvidenceRecord
                                        ? getStudentName(selectedEvidenceRecord)
                                        : "Rekod murid"}
                                </p>
                            </div>

                            <button className="closeButton" onClick={closeEvidenceModal}>
                                ×
                            </button>
                        </div>

                        {(selectedEvidenceRecord?.remarks || selectedEvidenceRecord?.achievement) && (
                            <div className="evidenceNoteBox">
                                <strong>Catatan</strong>
                                <p>
                                    {selectedEvidenceRecord?.remarks ||
                                        selectedEvidenceRecord?.achievement}
                                </p>
                            </div>
                        )}

                        {attachmentsLoading ? (
                            <div className="loadingBlock">Memuatkan eviden...</div>
                        ) : attachments.length === 0 ? (
                            <EmptyState
                                title="Tiada eviden"
                                description="Tiada gambar atau dokumen dilampirkan untuk rekod ini."
                            />
                        ) : (
                            <div className="attachmentList">
                                {attachments.map((attachment) => (
                                    <div className="attachmentItem" key={attachment.id}>
                                        <div>
                                            <div className="attachmentTitle">
                                                {attachment.title || attachment.file_name || "Eviden"}
                                            </div>
                                            <div className="attachmentMeta">
                                                {attachment.attachment_type || "fail"}
                                            </div>
                                        </div>

                                        <button
                                            className="smallButton"
                                            disabled={viewingAttachmentId === attachment.id}
                                            onClick={() => viewAttachmentImage(attachment.id)}
                                        >
                                            {viewingAttachmentId === attachment.id ? "Membuka..." : "Lihat"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {decryptedImage && (
                            <div className="imagePreviewBox">
                                <img src={decryptedImage} alt="Eviden PAJSK" />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = `
* {
    box-sizing: border-box;
}

.pajskPage {
    min-height: 100vh;
    background:
        radial-gradient(circle at top left, rgba(233, 64, 87, 0.10), transparent 28%),
        radial-gradient(circle at top right, rgba(242, 113, 33, 0.08), transparent 30%),
        #F8FAFC;
    color: ${PAJSK_COLORS.text};
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.pageShell {
    width: min(1480px, calc(100% - 48px));
    margin: 0 auto;
    padding: 28px 0 48px;
}

.heroPanel {
    min-height: 150px;
    border-radius: 18px;
    padding: 26px;
    background: linear-gradient(135deg, #8A2387, #E94057);
    color: #FFFFFF;
    display: flex;
    justify-content: space-between;
    gap: 24px;
    align-items: flex-end;
    box-shadow: 0 14px 36px rgba(15,23,42,0.12);
}

.eyebrow {
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.78);
}

.heroPanel h1 {
    margin: 10px 0 10px;
    font-size: 36px;
    line-height: 1.05;
    letter-spacing: -0.04em;
}

.heroPanel p {
    max-width: 780px;
    margin: 0;
    font-size: 15px;
    line-height: 1.7;
    font-weight: 650;
    color: rgba(255,255,255,0.86);
}

.heroActions {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
}

.primaryButton,
.ghostButton {
    height: 44px;
    border-radius: 12px;
    border: 0;
    padding: 0 18px;
    font-size: 13px;
    font-weight: 900;
    cursor: pointer;
    transition: 0.18s ease;
}

.primaryButton {
    color: #FFFFFF;
    background: linear-gradient(135deg, #E94057, #F27121);
    box-shadow: 0 12px 28px rgba(233,64,87,0.22);
}

.ghostButton {
    color: #111827;
    background: rgba(255,255,255,0.92);
    border: 1px solid rgba(255,255,255,0.7);
}

.primaryButton:hover,
.ghostButton:hover {
    transform: translateY(-1px);
}

.primaryButton:disabled,
.ghostButton:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.filtersPanel {
    margin-top: 18px;
    padding: 14px;
    border-radius: 16px;
    background: rgba(255,255,255,0.9);
    border: 1px solid rgba(238,242,247,0.95);
    box-shadow: 0 12px 34px rgba(15,23,42,0.05);
    display: grid;
    grid-template-columns: 180px 1fr 240px;
    gap: 12px;
}

.filterGroup,
.formGroup {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.filterGroup label,
.formGroup label {
    font-size: 11.5px;
    font-weight: 900;
    color: ${PAJSK_COLORS.muted};
}

input,
select,
textarea {
    width: 100%;
    border: 1px solid ${PAJSK_COLORS.border};
    background: #FFFFFF;
    color: ${PAJSK_COLORS.text};
    border-radius: 12px;
    min-height: 44px;
    padding: 0 13px;
    font-size: 13px;
    font-weight: 750;
    outline: none;
}

textarea {
    padding: 12px 13px;
    resize: vertical;
}

input:focus,
select:focus,
textarea:focus {
    border-color: rgba(233,64,87,0.45);
    box-shadow: 0 0 0 4px rgba(233,64,87,0.08);
}

.statsGrid {
    margin-top: 18px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
}

.statCard {
    min-height: 108px;
    background: #FFFFFF;
    border-radius: 16px;
    padding: 16px;
    border: 1px solid ${PAJSK_COLORS.border};
    box-shadow: 0 12px 34px rgba(15,23,42,0.05);
    position: relative;
    overflow: hidden;
}

.statCard:after {
    content: "";
    position: absolute;
    right: -42px;
    top: -42px;
    width: 92px;
    height: 92px;
    border-radius: 999px;
    background: rgba(233,64,87,0.08);
}

.statCard.purple:after {
    background: rgba(138,35,135,0.12);
}

.statCard.red:after {
    background: rgba(233,64,87,0.12);
}

.statCard.green:after {
    background: rgba(16,185,129,0.12);
}

.statCard.orange:after {
    background: rgba(249,115,22,0.13);
}

.statLabel {
    font-size: 12px;
    font-weight: 900;
    color: ${PAJSK_COLORS.muted};
}

.statValue {
    margin-top: 8px;
    font-size: 30px;
    font-weight: 950;
    letter-spacing: -0.04em;
}

.statHelper {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.4;
    font-weight: 700;
    color: ${PAJSK_COLORS.muted};
}

.mainGrid {
    margin-top: 18px;
    display: grid;
    grid-template-columns: 460px minmax(0, 1fr);
    gap: 18px;
    align-items: start;
}

.leftColumn,
.rightColumn {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.panel {
    background: rgba(255,255,255,0.94);
    border: 1px solid ${PAJSK_COLORS.border};
    border-radius: 18px;
    padding: 18px;
    box-shadow: 0 12px 34px rgba(15,23,42,0.05);
}

.panelHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
}

.panelHeader h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 950;
    letter-spacing: -0.03em;
}

.panelHeader p {
    margin: 5px 0 0;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1.45;
    color: ${PAJSK_COLORS.muted};
}

.activityPanel {
    padding: 16px;
}

.compactHeader {
    margin-bottom: 12px;
}

.plainCount {
    font-size: 12px;
    font-weight: 750;
    color: #6B7280;
    background: #F9FAFB;
    border: 1px solid #EEF2F7;
    border-radius: 8px;
    padding: 6px 9px;
    white-space: nowrap;
}

.activityTableWrap {
    border: 0;
    border-radius: 0;
    overflow: visible;
    background: transparent;
}

.activityCardList {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.activityCard {
    width: 100%;
    border: 1px solid #E8EEF6;
    background: #FFFFFF;
    border-radius: 16px;
    padding: 13px;
    text-align: left;
    cursor: pointer;
    transition: 0.16s ease;
    box-shadow: 0 8px 20px rgba(15,23,42,0.035);
}

.activityCard:hover {
    border-color: rgba(233,64,87,0.24);
    box-shadow: 0 12px 28px rgba(15,23,42,0.07);
    transform: translateY(-1px);
}

.activityCard.active {
    border-color: rgba(233,64,87,0.42);
    background: linear-gradient(180deg, #FFFFFF, #FFF8F9);
    box-shadow: 0 14px 34px rgba(233,64,87,0.10);
}

.activityCardTop {
    display: flex;
    align-items: flex-start;
    gap: 11px;
}

.activityIcon {
    width: 38px;
    height: 38px;
    border-radius: 13px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    background: linear-gradient(135deg, #FFF1F3, #FFF7ED);
    color: #E94057;
    font-size: 13px;
    font-weight: 950;
    border: 1px solid rgba(233,64,87,0.12);
}

.activityCardMain {
    flex: 1;
    min-width: 0;
}

.activityTitleRow {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.activityTitleRow h3 {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.35;
    font-weight: 950;
    color: #0F172A;
    letter-spacing: -0.015em;
}

.activityMetaLine {
    margin-top: 5px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 11.2px;
    line-height: 1.35;
    font-weight: 750;
    color: #64748B;
}

.pendingBadge {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    height: 22px;
    border-radius: 999px;
    padding: 0 8px;
    font-size: 10.5px;
    font-weight: 950;
    white-space: nowrap;
    border: 1px solid transparent;
}

.pendingBadge.hasPending {
    background: #FFF7ED;
    color: #C2410C;
    border-color: #FED7AA;
}

.pendingBadge.noPending {
    background: #F1F5F9;
    color: #64748B;
    border-color: #E2E8F0;
}

.activityStatsMini {
    margin-top: 11px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 7px;
}

.activityStatsMini div {
    border-radius: 12px;
    background: #F8FAFC;
    border: 1px solid #EEF2F7;
    padding: 8px 9px;
}

.activityStatsMini strong {
    display: block;
    font-size: 14px;
    line-height: 1;
    font-weight: 950;
    color: #111827;
}

.activityStatsMini span {
    display: block;
    margin-top: 4px;
    font-size: 10.2px;
    line-height: 1.2;
    font-weight: 850;
    color: #94A3B8;
}
.tableHeader {
    align-items: flex-end;
}

.tableFilters {
    display: grid;
    grid-template-columns: 220px 160px;
    gap: 10px;
}

.tableWrap {
    overflow: visible;
}

.recordList {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.recordCard {
    position: relative;
    background: #FFFFFF;
    border: 1px solid #E8EEF6;
    border-radius: 18px;
    padding: 16px;
    box-shadow: 0 10px 26px rgba(15,23,42,0.045);
    transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
    overflow: hidden;
}

.recordCard::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: linear-gradient(180deg, #E94057, #F27121);
    opacity: 0.9;
}

.recordCard:hover {
    border-color: rgba(233,64,87,0.24);
    box-shadow: 0 16px 38px rgba(15,23,42,0.075);
    transform: translateY(-1px);
}

.recordCardTop {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding-left: 4px;
}

.studentCell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.recordStudentCell {
    align-items: center;
    min-width: 0;
}

.recordSummaryRow {
    margin-top: 16px;
}
    .recordCard {
    padding: 18px;
}
.avatar {
    width: 46px;
    height: 46px;
    min-width: 46px;
    max-width: 46px;
    min-height: 46px;
    max-height: 46px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #FFF1F3, #FFF7ED);
    color: #E94057;
    font-size: 14px;
    font-weight: 950;
    flex: 0 0 46px;
    border: 1px solid rgba(233,64,87,0.12);
    overflow: hidden;
    line-height: 1;
}

.avatar img,
.studentAvatarImg {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: cover;
    display: block;
    flex: 0 0 auto;
}

.avatarFallback {
    background: linear-gradient(135deg, #FFF1F3, #FFF7ED);
    color: #E94057;
}
.studentTextBlock {
    min-width: 0;
}

.recordStudentName {
    font-size: 15.5px;
    line-height: 1.25;
    font-weight: 950;
    color: #0F172A;
    word-break: break-word;
}

.recordMetaLine {
    margin-top: 5px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 750;
    color: #64748B;
}

.metaDot {
    color: #CBD5E1;
}

.recordSummaryRow {
    margin-top: 14px;
    display: grid;
    grid-template-columns: 190px 150px minmax(180px, 1fr) minmax(180px, 1fr);
    gap: 10px;
}

.summaryItem {
    min-width: 0;
    border: 1px solid #EEF2F7;
    background: #F8FAFC;
    border-radius: 14px;
    padding: 10px 12px;
}

.summaryItem span,
.recordNotePanel span {
    display: block;
    font-size: 10.5px;
    line-height: 1.2;
    font-weight: 950;
    letter-spacing: 0.055em;
    text-transform: uppercase;
    color: #94A3B8;
}

.summaryItem strong,
.recordNotePanel strong {
    display: block;
    margin-top: 6px;
    font-size: 12.8px;
    line-height: 1.45;
    font-weight: 850;
    color: #111827;
    white-space: normal;
    word-break: break-word;
}

.summaryItem:first-child strong {
    font-size: 15px;
    font-weight: 950;
    color: #0F172A;
}

.personItem {
    background: #FFFFFF;
}

.recordNotePanel {
    margin-top: 10px;
    border: 1px solid #EEF2F7;
    background: linear-gradient(180deg, #FFFFFF, #F9FAFB);
    border-radius: 14px;
    padding: 11px 12px;
}

.recordNotePanel strong {
    color: #374151;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.recordCardBottom {
    margin-top: 13px;
    padding-top: 12px;
    border-top: 1px solid #F1F5F9;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
}

.evidenceSummary {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 12px;
    font-weight: 850;
    color: #64748B;
}

.evidenceDot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #E94057;
    box-shadow: 0 0 0 4px rgba(233,64,87,0.10);
    flex: 0 0 auto;
}

.recordActions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}
.statusBadge {
    display: inline-flex;
    align-items: center;
    height: 28px;
    border-radius: 999px;
    padding: 0 10px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
}

.statusDraft {
    background: #F3F4F6;
    color: #374151;
}

.statusSubmitted {
    background: #EFF6FF;
    color: #1D4ED8;
}

.statusVerified {
    background: #ECFDF5;
    color: #047857;
}

.statusRejected {
    background: #FEF2F2;
    color: #B91C1C;
}

.evidenceButton {
    height: 32px;
    border: 0;
    border-radius: 10px;
    padding: 0 11px;
    background: #FFF1F3;
    color: #E94057;
    font-size: 11.5px;
    font-weight: 900;
    cursor: pointer;
}

.evidenceButton:hover {
    background: #FFE4E8;
}

.noEvidenceText {
    font-size: 11.5px;
    font-weight: 750;
    color: #9CA3AF;
}

.verifyButton,
.rejectButton {
    height: 32px;
    border-radius: 10px;
    border: 0;
    padding: 0 11px;
    font-size: 11.5px;
    font-weight: 950;
    cursor: pointer;
}

.verifyButton {
    color: #047857;
    background: #ECFDF5;
}

.rejectButton {
    color: #B91C1C;
    background: #FEF2F2;
}

.verifyButton:disabled,
.rejectButton:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.emptyState {
    min-height: 140px;
    border-radius: 14px;
    border: 1px dashed rgba(233,64,87,0.25);
    background: #FFF8F9;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px;
}

.emptyIcon {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    background: #FFFFFF;
    display: grid;
    place-items: center;
    color: ${PAJSK_COLORS.primary};
    font-weight: 950;
}

.emptyTitle {
    font-size: 14px;
    font-weight: 950;
}

.emptyDescription {
    margin-top: 4px;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1.45;
    color: ${PAJSK_COLORS.muted};
}

.loadingBlock {
    min-height: 120px;
    display: grid;
    place-items: center;
    border-radius: 14px;
    background: #F9FAFB;
    color: ${PAJSK_COLORS.muted};
    font-size: 13px;
    font-weight: 850;
}

.paginationRow {
    margin-top: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
}

.paginationRow button {
    height: 34px;
    border: 1px solid ${PAJSK_COLORS.border};
    background: #FFFFFF;
    border-radius: 10px;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
}

.paginationRow button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.paginationRow span {
    font-size: 12px;
    font-weight: 850;
    color: ${PAJSK_COLORS.muted};
}

.modalOverlay {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,0.48);
    display: grid;
    place-items: center;
    padding: 24px;
    z-index: 999;
}

.modalCard {
    width: min(720px, 100%);
    border-radius: 18px;
    padding: 22px;
    background: #FFFFFF;
    box-shadow: 0 28px 80px rgba(15,23,42,0.25);
}

.evidenceModal {
    max-height: 86vh;
    overflow: auto;
}

.modalHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 18px;
}

.modalHeader h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
    letter-spacing: -0.03em;
}

.modalHeader p {
    margin: 5px 0 0;
    font-size: 12.5px;
    color: ${PAJSK_COLORS.muted};
    font-weight: 700;
}

.closeButton {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    border: 0;
    background: #F3F4F6;
    color: ${PAJSK_COLORS.text};
    font-size: 24px;
    cursor: pointer;
}

.evidenceNoteBox {
    border: 1px solid #EEF2F7;
    background: #F9FAFB;
    border-radius: 14px;
    padding: 12px 14px;
    margin-bottom: 14px;
}

.evidenceNoteBox strong {
    display: block;
    font-size: 12px;
    font-weight: 900;
    color: #111827;
    margin-bottom: 5px;
}

.evidenceNoteBox p {
    margin: 0;
    font-size: 12.5px;
    font-weight: 650;
    line-height: 1.5;
    color: #4B5563;
}

.attachmentList {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.attachmentItem {
    border: 1px solid #EEF2F7;
    background: #FFFFFF;
    border-radius: 14px;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.attachmentTitle {
    font-size: 13px;
    font-weight: 850;
    color: #111827;
}

.attachmentMeta {
    margin-top: 3px;
    font-size: 11.5px;
    font-weight: 650;
    color: #6B7280;
}

.smallButton {
    height: 32px;
    border: 0;
    border-radius: 10px;
    padding: 0 12px;
    background: #111827;
    color: #FFFFFF;
    font-size: 11.5px;
    font-weight: 900;
    cursor: pointer;
}

.smallButton:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.imagePreviewBox {
    margin-top: 14px;
    border: 1px solid #EEF2F7;
    border-radius: 14px;
    overflow: hidden;
    background: #F9FAFB;
}

.imagePreviewBox img {
    display: block;
    width: 100%;
    max-height: 520px;
    object-fit: contain;
    background: #111827;
}

.formGrid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 13px;
}

.formGroup.full {
    grid-column: 1 / -1;
}

.modalActions {
    margin-top: 18px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.modalActions .ghostButton {
    background: #F3F4F6;
}

@media (max-width: 1180px) {
    .mainGrid {
        grid-template-columns: 1fr;
    }

    .statsGrid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 820px) {
    .pageShell {
        width: min(100% - 24px, 1480px);
    }

    .heroPanel {
        display: block;
    }

    .heroActions {
        margin-top: 20px;
    }

    .heroPanel h1 {
        font-size: 30px;
    }

    .filtersPanel,
    .statsGrid,
    .tableFilters,
    .formGrid,
    .recordInfoGrid {
        grid-template-columns: 1fr;
    }

    .recordCardTop,
    .recordCardBottom {
        flex-direction: column;
        align-items: stretch;
    }

    .recordActions {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
    }

    .recordActions .evidenceButton {
        grid-column: 1 / -1;
    }

    .formGroup.full {
        grid-column: auto;
    }
        .pendingBadge {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    height: 22px;
    border-radius: 999px;
    padding: 0 8px;
    font-size: 10.5px;
    font-weight: 950;
    white-space: nowrap;
    border: 1px solid transparent;
}

.pendingBadge.hasPending {
    background: #FFF7ED;
    color: #C2410C;
    border-color: #FED7AA;
}

.pendingBadge.noPending {
    background: #F1F5F9;
    color: #64748B;
    border-color: #E2E8F0;
}
}
`;

export default PAJSKDashboard;